import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  FormControl,
  Chip,
  Container,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Fade,
  Zoom,
  useTheme,
  alpha
} from "@mui/material";
import {
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Extension as PuzzlePieceIcon,
  Message as MessageSquareIcon,
  PlayArrow as PlayIcon,
  AccessTime as AccessTimeIcon,
  Headphones as HeadphonesIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Translate as TranslateIcon,
  Quiz as QuizIcon
} from '@mui/icons-material';
import CardPreprocessor from "../../../utils/CardPreprocessor";
import ReviewMode from "./ReviewMode";
import ReviewSummary from "./ReviewSummary";
import api from "../../../api";

/**
 * Professional ReviewModeAdapter with clean, consistent design
 */
const ReviewModeAdapter = ({ token, fetchImages = false }) => {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // UI State
  const [loading, setLoading] = useState(true);
  const [showOptions, setShowOptions] = useState(true);
  const [showSummary, setShowSummary] = useState(false);

  // Review Options
  const [enableShadowing, setEnableShadowing] = useState(true);
  const [enableAudioTranscription, setEnableAudioTranscription] = useState(true);
  const [enableAudioTranslation, setEnableAudioTranslation] = useState(true);
  const [tokenMode, setTokenMode] = useState('easy');

  // Data
  const [deck, setDeck] = useState(null);
  const [rawCards, setRawCards] = useState([]);
  const [processedCards, setProcessedCards] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // Enhanced metrics with variant counting
  const [reviewMetrics, setReviewMetrics] = useState({
    sentenceQuestions: 0,
    wordCount: 0,
    totalCards: 0,
    estimatedTime: "Calculating..."
  });

  /**
   * Fetch deck and cards on mount
   */
  useEffect(() => {
    const fetchData = async () => {
      if (!deckId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch deck details
        const deckResponse = await api.get(`/decks/${deckId}`);
        setDeck(deckResponse.data.deck);

        // Fetch cards for review
        const reviewEndpoint = fetchImages
          ? `${deckId}/review-v2`
          : `${deckId}/review-v2?skipImages=true`;
        const reviewResponse = await api.get(`/decks/${reviewEndpoint}`);
        const dueCards = reviewResponse.data.dueCards || [];

        console.log('Fetched due cards:', dueCards.length);

        // Simple enhancement: ensure cards have essential properties
        const enhancedCards = dueCards.map(card => ({
          ...card,
          uniqueCardId: card.uniqueCardId || `${card.cardId}-${card.questionType || 'text'}-${Math.random().toString(36).substring(2, 9)}`,
          ...(fetchImages ? {} : {
            steps: card.steps?.map(step => ({
              ...step,
              images: step.images?.map(img => ({
                ...img,
                imageUrl: img.imageUrl || null,
                dataUrl: null
              }))
            }))
          })
        }));

        setRawCards(enhancedCards);

      } catch (error) {
        console.error("Error fetching data:", error);
        setRawCards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [deckId, fetchImages]);

  /**
   * Calculate enhanced metrics with variant counting
   */
  const calculateDisplayMetrics = (cards, options = {}) => {
    const {
      enableShadowing = true,
      enableAudioTranscription = true,
      enableAudioTranslation = true
    } = options;

    const sentenceIds = new Set();
    let wordCount = 0;
    let totalSentenceQuestions = 0;

    cards.forEach(card => {
      if (card.metadata?.sentenceId) {
        sentenceIds.add(card.metadata.sentenceId);
      } else {
        wordCount++;
      }
    });

    // Calculate total sentence questions based on enabled options
    sentenceIds.forEach(sentenceId => {
      const sentenceCards = cards.filter(card => card.metadata?.sentenceId === sentenceId);
      const hasAudio = sentenceCards.some(card => card.audioUrl || (card.audio && card.audio.path));
      
      let questionsForThisSentence = 1; // Base sentence card
      questionsForThisSentence++; // Token reordering variant (always available for sentences)
      
      if (hasAudio) {
        if (enableShadowing) questionsForThisSentence++;
        if (enableAudioTranscription) questionsForThisSentence++;
        if (enableAudioTranslation) questionsForThisSentence++;
      }
      
      totalSentenceQuestions += questionsForThisSentence;
    });

    // Add shadowing variants for vocabulary words if enabled
    if (enableShadowing) {
      const vocabWordsWithAudio = cards.filter(card => 
        card.metadata?.type === "vocabulary" && 
        (card.audioUrl || (card.audio && card.audio.path))
      ).length;
      wordCount += vocabWordsWithAudio; // These get added to word count as additional practice
    }

    const totalCards = totalSentenceQuestions + wordCount;

    return {
      sentenceQuestions: totalSentenceQuestions,
      wordCount,
      totalCards,
      estimatedTime: calculateEstimatedTime(cards, options)
    };
  };

  /**
   * Recalculate metrics when options change
   */
  useEffect(() => {
    if (rawCards.length > 0) {
      const metrics = calculateDisplayMetrics(rawCards, {
        enableShadowing,
        enableAudioTranscription,
        enableAudioTranslation,
        tokenMode
      });
      setReviewMetrics(metrics);
    }
  }, [rawCards, enableShadowing, enableAudioTranscription, enableAudioTranslation, tokenMode]);

  /**
   * Enhanced time calculation based on actual card variants
   */
  const calculateEstimatedTime = (cards, options = {}) => {
    if (!cards || cards.length === 0) return "0 minutes";

    const { enableShadowing, enableAudioTranscription, enableAudioTranslation } = options;

    let sentenceBaseTime = 0;
    let shadowingTime = 0;
    let audioTranscriptionTime = 0;
    let audioTranslationTime = 0;
    let tokenReorderingTime = 0;
    let vocabularyTime = 0;

    const sentenceIds = new Set();

    // Process sentences
    cards.forEach(card => {
      if (card.metadata?.sentenceId) {
        sentenceIds.add(card.metadata.sentenceId);
      } else if (card.metadata?.type === "vocabulary") {
        vocabularyTime += 8; // 8 seconds per vocab word
        if (enableShadowing && (card.audioUrl || (card.audio && card.audio.path))) {
          shadowingTime += 15; // Additional time for vocab shadowing
        }
      } else {
        vocabularyTime += 8; // Other word types
      }
    });

    // Calculate time for each sentence and its variants
    sentenceIds.forEach(sentenceId => {
      const sentenceCards = cards.filter(card => card.metadata?.sentenceId === sentenceId);
      const hasAudio = sentenceCards.some(card => card.audioUrl || (card.audio && card.audio.path));
      
      sentenceBaseTime += 12; // Base time for sentence comprehension
      tokenReorderingTime += 18; // Token reordering variant
      
      if (hasAudio) {
        if (enableShadowing) shadowingTime += 25;
        if (enableAudioTranscription) audioTranscriptionTime += 20;
        if (enableAudioTranslation) audioTranslationTime += 15;
      }
    });

    // Total time calculation with buffers
    const baseTime = sentenceBaseTime + vocabularyTime;
    const interactiveTime = shadowingTime + audioTranscriptionTime + audioTranslationTime + tokenReorderingTime;
    
    // Add 20% buffer for loading, thinking, errors
    let totalTimeSeconds = (baseTime + interactiveTime) * 1.2;
    let minutes = Math.ceil(totalTimeSeconds / 60);

    if (minutes === 0) return "Less than 1 minute";

    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      minutes = minutes % 60;

      if (minutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${minutes}m`;
    }

    return `${minutes} min`;
  };

  /**
   * Start review session
   */
  const startReview = () => {
    if (rawCards.length === 0) return;

    try {
      const processed = processCardsForReview(rawCards, {
        enableShadowing,
        enableAudioTranscription,
        enableAudioTranslation,
        tokenMode,
        fetchImages
      });

      console.log('Processed cards for review:', processed.length);
      setProcessedCards(processed);
      setShowOptions(false);
    } catch (error) {
      console.error("Error processing cards for review:", error);
    }
  };

  /**
   * Process cards with selected options
   */
  const processCardsForReview = (cards, options = {}) => {
    console.log(`Processing ${cards.length} cards with options:`, options);

    try {
      const processingOptions = {
        ...options,
        fetchImages: options.fetchImages
      };

      const processedByPreprocessor = CardPreprocessor.processCards(cards, processingOptions);
      console.log(`CardPreprocessor created ${processedByPreprocessor.length} processed cards`);

      const expandedCards = CardPreprocessor.expandCardsWithVariants(processedByPreprocessor);
      console.log(`Expanded to ${expandedCards.length} individual cards`);

      // Preserve audio properties
      expandedCards.forEach(card => {
        if (card.questionType === 'shadowing' || card.questionType === 'audio') {
          const originalCard = processedByPreprocessor.find(c =>
            (c.cardId === card.cardId) ||
            (c.variants && c.variants.some(v => v.variantId === card.variantId))
          );

          if (originalCard) {
            ['audioCdnUrl', 'audioUrl', 'audioData', 'audio'].forEach(prop => {
              if (originalCard[prop] && !card[prop]) {
                card[prop] = originalCard[prop];
              }
            });

            if (originalCard.variants && card.variantId) {
              const matchingVariant = originalCard.variants.find(v => v.variantId === card.variantId);
              if (matchingVariant) {
                ['audioCdnUrl', 'audioUrl', 'audioData', 'audio'].forEach(prop => {
                  if (matchingVariant[prop] && !card[prop]) {
                    card[prop] = matchingVariant[prop];
                  }
                });
              }
            }
          }
        }
      });

      // Strip image data if not fetching images
      if (!options.fetchImages) {
        expandedCards.forEach(card => {
          if (card.steps) {
            card.steps.forEach(step => {
              if (step.images) {
                step.images = step.images.map(img => ({
                  ...img,
                  imageUrl: img.imageUrl || null,
                  dataUrl: null,
                }));
              }
            });
          }
        });
      }

      const finalCards = CardPreprocessor.distributeAndShuffleCards(processedByPreprocessor);
      console.log(`Final processed cards: ${finalCards.length}`);
      return finalCards;

    } catch (error) {
      console.error('Error in processCardsForReview:', error);
      return cards;
    }
  };

  /**
   * Handle review completion
   */
  const handleReviewComplete = (summaryData) => {
    console.log("Review completed, received summary data:", summaryData);
    setSummaryData(summaryData);
    setShowSummary(true);
  };

  /**
   * Handle early termination  
   */
  const handleEarlyTermination = (summaryData) => {
    console.log("Review terminated early, received summary data:", summaryData);
    setSummaryData(summaryData);
    setShowSummary(true);
  };

  /**
   * Handle returning to course/deck after review
   */
  const handleReturnAfterReview = () => {
    if (deck && deck.courseId) {
      navigate(`/courses/${deck.courseId}`);
    } else {
      navigate(-1);
    }
  };

  /**
   * Handle restarting review
   */
  const handleRestartReview = () => {
    setShowSummary(false);
    setShowOptions(true);
    setSummaryData(null);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{
        width: '100vw',
        minHeight: '100vh',
        backgroundColor: 'rgb(216, 241, 247)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'scale(0.8)',
        transformOrigin: 'center',
        margin: 0,
        boxSizing: 'border-box'
      }}>
        <Card sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: '#ffffff',
          textAlign: 'center',
          border: '1px solid #bfdbfe',
          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)'
        }}>
          <CircularProgress size={40} sx={{ mb: 2, color: '#2563eb' }} />
          <Typography variant="h6" sx={{ color: '#1e3a8a', fontWeight: 500, fontSize: '1rem' }}>
            Preparing your review session...
          </Typography>
        </Card>
      </Box>
    );
  }

  // No cards state
  if (rawCards.length === 0) {
    return (
      <Box sx={{
        width: '100vw',
        minHeight: '100vh',
        backgroundColor: 'rgb(216, 241, 247)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        transform: 'scale(0.8)',
        transformOrigin: 'center',
        margin: 0,
        boxSizing: 'border-box'
      }}>
        <Card sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: '#ffffff',
          textAlign: 'center',
          border: '1px solid #bfdbfe',
          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
          maxWidth: 400
        }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#1e3a8a', fontWeight: 600, fontSize: '1.5rem' }}>
            All Caught Up! 🎉
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#3730a3', fontSize: '0.95rem' }}>
            You've completed all your reviews for now. Great job on staying consistent!
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: '#2563eb',
              borderRadius: 1.5,
              px: 3,
              py: 1.5,
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1d4ed8',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Return to Dashboard
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'rgb(216, 241, 247)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        py: 2,
        px: 2,
        transformOrigin: 'top center',
        margin: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        minHeight: '100vh'
      }}
    >
      <Container maxWidth="md">
        {showSummary ? (
          <ReviewSummary
            summaryData={summaryData}
            onRestart={handleRestartReview}
            onReturn={handleReturnAfterReview}
          />
        ) : showOptions ? (
          <Fade in timeout={600}>
            <Card sx={{
              borderRadius: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #bfdbfe',
              overflow: 'hidden',
              boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.1)'
            }}>
              {/* Header Section */}
              <Box sx={{
                backgroundColor: '#1e3a8a',
                p: 3,
                textAlign: 'center'
              }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mb: 0.5,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                  }}
                >
                  Ready to Review?
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontWeight: 400,
                  fontSize: '1rem'
                }}>
                  {deck?.title || "Your Deck"}
                </Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Stats Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#1e3a8a',
                    textAlign: 'center',
                    fontSize: '1.25rem'
                  }}>
                    Review Overview
                  </Typography>
                  
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                    gap: 2,
                    mb: 2
                  }}>
                    {/* Sentence Questions */}
                    <Card sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: '#dbeafe',
                      border: '1px solid #93c5fd',
                      borderRadius: 2
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: '2rem', color: '#1e40af' }}>
                        {reviewMetrics.sentenceQuestions}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 500, fontSize: '0.875rem' }}>
                        Sentence Questions
                      </Typography>
                    </Card>

                    {/* Words */}
                    <Card sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: '#d1fae5',
                      border: '1px solid #86efac',
                      borderRadius: 2
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: '2rem', color: '#047857' }}>
                        {reviewMetrics.wordCount}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 500, fontSize: '0.875rem' }}>
                        Words
                      </Typography>
                    </Card>

                    {/* Total */}
                    <Card sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: '#e0e7ff',
                      border: '1px solid #a5b4fc',
                      borderRadius: 2
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: '2rem', color: '#4338ca' }}>
                        {reviewMetrics.totalCards}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#3730a3', fontWeight: 500, fontSize: '0.875rem' }}>
                        Total Cards
                      </Typography>
                    </Card>
                  </Box>

                  {/* Time Estimate */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: '1rem' }} />}
                      label={`Est. time: ${reviewMetrics.estimatedTime}`}
                      sx={{
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        border: '1px solid #f59e0b',
                        '& .MuiChip-icon': { color: '#92400e' }
                      }}
                    />
                  </Box>
                </Box>

                {/* Options Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#1e3a8a',
                    textAlign: 'center',
                    fontSize: '1.25rem'
                  }}>
                    Customize Your Practice
                  </Typography>

                  {/* Audio Options */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ 
                      mb: 1.5, 
                      fontWeight: 600, 
                      color: '#1e40af',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}>
                      <HeadphonesIcon sx={{ fontSize: '1.25rem' }} />
                      Audio Practice Options
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                      gap: 1.5 
                    }}>
                      {/* Shadowing */}
                      <Card sx={{
                        p: 2,
                        backgroundColor: enableShadowing ? '#dcfce7' : 'rgb(216, 241, 247)',
                        border: enableShadowing ? '2px solid #10b981' : '1px solid #bfdbfe',
                        borderRadius: 2,
                        transition: 'all 0.2s ease'
                      }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableShadowing}
                              onChange={(e) => setEnableShadowing(e.target.checked)}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#10b981',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#10b981',
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <RecordVoiceOverIcon sx={{ 
                                color: enableShadowing ? '#047857' : '#2563eb',
                                fontSize: '1.5rem'
                              }} />
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: enableShadowing ? '#047857' : '#1e40af',
                                textAlign: 'center',
                                fontSize: '0.875rem'
                              }}>
                                Shadowing
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            m: 0, 
                            width: '100%',
                            justifyContent: 'center'
                          }}
                        />
                      </Card>

                      {/* Audio Transcription */}
                      <Card sx={{
                        p: 2,
                        backgroundColor: enableAudioTranscription ? '#dbeafe' : 'rgb(216, 241, 247)',
                        border: enableAudioTranscription ? '2px solid #3b82f6' : '1px solid #bfdbfe',
                        borderRadius: 2,
                        transition: 'all 0.2s ease'
                      }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableAudioTranscription}
                              onChange={(e) => setEnableAudioTranscription(e.target.checked)}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#3b82f6',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#3b82f6',
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <HeadphonesIcon sx={{ 
                                color: enableAudioTranscription ? '#1e40af' : '#2563eb',
                                fontSize: '1.5rem'
                              }} />
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: enableAudioTranscription ? '#1e40af' : '#1e40af',
                                textAlign: 'center',
                                fontSize: '0.875rem'
                              }}>
                                Transcription
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            m: 0, 
                            width: '100%',
                            justifyContent: 'center'
                          }}
                        />
                      </Card>

                      {/* Audio Translation */}
                      <Card sx={{
                        p: 2,
                        backgroundColor: enableAudioTranslation ? '#e0e7ff' : 'rgb(216, 241, 247)',
                        border: enableAudioTranslation ? '2px solid #6366f1' : '1px solid #bfdbfe',
                        borderRadius: 2,
                        transition: 'all 0.2s ease'
                      }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableAudioTranslation}
                              onChange={(e) => setEnableAudioTranslation(e.target.checked)}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#6366f1',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#6366f1',
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <TranslateIcon sx={{ 
                                color: enableAudioTranslation ? '#4338ca' : '#2563eb',
                                fontSize: '1.5rem'
                              }} />
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: enableAudioTranslation ? '#4338ca' : '#1e40af',
                                textAlign: 'center',
                                fontSize: '0.875rem'
                              }}>
                                Translation
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            m: 0, 
                            width: '100%',
                            justifyContent: 'center'
                          }}
                        />
                      </Card>
                    </Box>
                  </Box>

                  {/* Sentence Mode */}
                  <Card sx={{
                    p: 2,
                    backgroundColor: 'rgb(216, 241, 247)',
                    borderRadius: 2,
                    border: '1px solid #bfdbfe'
                  }}>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      fontWeight: 600, 
                      color: '#1e40af',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'center',
                      fontSize: '1rem'
                    }}>
                      <QuizIcon sx={{ fontSize: '1.25rem' }} />
                      Sentence Reconstruction Mode
                    </Typography>
                    
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup
                        value={tokenMode}
                        onChange={(e) => setTokenMode(e.target.value)}
                        sx={{ 
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 2
                        }}
                      >
                        <Card sx={{
                          p: 2,
                          backgroundColor: tokenMode === 'easy' ? '#dbeafe' : 'white',
                          borderRadius: 2,
                          border: tokenMode === 'easy' ? '2px solid #3b82f6' : '1px solid #bfdbfe',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            backgroundColor: 'rgb(216, 241, 247)'
                          }
                        }}>
                          <FormControlLabel
                            value="easy"
                            control={<Radio size="small" sx={{ 
                              color: tokenMode === 'easy' ? '#3b82f6' : '#2563eb',
                              '&.Mui-checked': { color: '#3b82f6' }
                            }} />}
                            label={
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, textAlign: 'center' }}>
                                <PuzzlePieceIcon sx={{ 
                                  color: tokenMode === 'easy' ? '#1e40af' : '#2563eb',
                                  fontSize: '1.5rem'
                                }} />
                                <Box>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 600,
                                    color: tokenMode === 'easy' ? '#1e40af' : '#1e40af',
                                    mb: 0.25,
                                    fontSize: '0.875rem'
                                  }}>
                                    Assisted Mode
                                  </Typography>
                                  <Typography variant="caption" sx={{ 
                                    color: tokenMode === 'easy' ? '#3730a3' : '#3730a3',
                                    fontSize: '0.75rem'
                                  }}>
                                    Drag & drop tokens
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            sx={{ 
                              m: 0, 
                              width: '100%',
                              justifyContent: 'center'
                            }}
                          />
                        </Card>

                        <Card sx={{
                          p: 2,
                          backgroundColor: tokenMode === 'hard' ? '#fef3c7' : 'white',
                          borderRadius: 2,
                          border: tokenMode === 'hard' ? '2px solid #f59e0b' : '1px solid #bfdbfe',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#f59e0b',
                            backgroundColor: '#fffbeb'
                          }
                        }}>
                          <FormControlLabel
                            value="hard"
                            control={<Radio size="small" sx={{ 
                              color: tokenMode === 'hard' ? '#f59e0b' : '#2563eb',
                              '&.Mui-checked': { color: '#f59e0b' }
                            }} />}
                            label={
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, textAlign: 'center' }}>
                                <MessageSquareIcon sx={{ 
                                  color: tokenMode === 'hard' ? '#d97706' : '#2563eb',
                                  fontSize: '1.5rem'
                                }} />
                                <Box>
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: 600,
                                    color: tokenMode === 'hard' ? '#d97706' : '#1e40af',
                                    mb: 0.25,
                                    fontSize: '0.875rem'
                                  }}>
                                    Challenge Mode
                                  </Typography>
                                  <Typography variant="caption" sx={{ 
                                    color: tokenMode === 'hard' ? '#92400e' : '#3730a3',
                                    fontSize: '0.75rem'
                                  }}>
                                    Type complete sentences
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            sx={{ 
                              m: 0, 
                              width: '100%',
                              justifyContent: 'center'
                            }}
                          />
                        </Card>
                      </RadioGroup>
                    </FormControl>
                  </Card>
                </Box>

                {/* Start Button */}
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayIcon />}
                    onClick={startReview}
                    disabled={rawCards.length === 0}
                    sx={{
                      backgroundColor: '#2563eb',
                      borderRadius: 2,
                      py: 1.5,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                      '&:hover': {
                        backgroundColor: '#1d4ed8',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)'
                      },
                      '&:disabled': {
                        backgroundColor: '#60a5fa',
                        color: 'white',
                        opacity: 0.6
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Start Review Session
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        ) : (
          <ReviewMode
            token={token}
            tokenMode={tokenMode}
            onReviewComplete={handleReviewComplete}
            initialCards={processedCards}
            deck={deck}
            rawCards={rawCards}
            reviewMetrics={reviewMetrics}
            onEndEarly={handleEarlyTermination}
            fetchImages={fetchImages}
          />
        )}
      </Container>
    </Box>
  );
};

export default ReviewModeAdapter;